import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CropLandscapeIcon from "@mui/icons-material/CropLandscape";
import CropPortraitIcon from "@mui/icons-material/CropPortrait";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import LoopIcon from "@mui/icons-material/Loop";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlayOutlined";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import { PageContainer, PageHeader } from "../components/PageHeader";
import SignageScheduleBuilder from "../components/SignageScheduleBuilder";
import SignageDayPlanPicker, {
  describePlan,
} from "../components/SignageDayPlanPicker";
import SignagePlaylistDialog from "../components/SignagePlaylistDialog";
import {
  foyerApi,
  type AssetSummary,
  type MediaLayer,
  type SignageScopeRef,
} from "../http/foyer";
import { listSignageScreensIn } from "../utils/signageScreens";
import { openSignagePreview } from "../utils/preview";
import {
  byUrl,
  isOwnedBy,
  loopSeconds,
  ownerLabel,
  thumbnailFor,
} from "../utils/signageItems";
import {
  ORIENTATION_LABELS,
  durationLabel,
  orientationOfResolution,
} from "@shared/media";
import { tokens } from "../theme";
import type { DayOfWeek } from "@shared/types/foyer";
import type { SignageDayPlan, SignagePlaylist } from "@shared/types/signage";

/**
 * Plan content - assign a playlist to each weekday, per screen.
 *
 * PRESENTATIONAL ONLY for now. Every prop this page needs is already served by
 * the signage API (screens, playlists, `SignageAssignment.weekly`), but the
 * wiring is a deliberate second pass - the shapes below are the contract that
 * pass has to satisfy.
 *
 * The design assigns a PLAYLIST to a day and labels it "loops all day". Our
 * model assigns a DAY PLAN (a list of `HH:mm` blocks). The two reconcile
 * cleanly: an all-day assignment is a day plan holding one 00:00-24:00 block,
 * created behind the scenes. That keeps the simple case simple - an operator
 * never meets the day-plan concept until they actually want two playlists in
 * one day - without giving up intraday scheduling, which the legacy autoplayers
 * already support and therefore must keep working.
 */

/** A screen as this page needs it. */
/**
 * Something a week can be scheduled ON.
 *
 * Usually a screen. It can also be a GROUP - "every treadmill screen" - which
 * writes one week that every member screen inherits, instead of the same week
 * typed out fifty times. Both live in one list because the grid to the right of
 * them is identical either way; only where the week is stored differs.
 */
export interface PlanScreen {
  id: string;
  /** Absent means "screen" - the shape predates groups. */
  kind?: "screen" | "group";
  /**
   * The location this screen stands in - assignment writes are premise-keyed.
   *
   * Empty for a GROUP: a group can span locations, so there is no one premise
   * to key its week by, and its assignment row is keyed by the group instead.
   */
  premiseId: string;
  name: string;
  /** Short badge under the name - the screen's group or content tag. */
  tag?: string;
  resolution?: string;
}

/** One day's assignment. `null` playlist = nothing scheduled that day. */
export interface PlanDay {
  day: DayOfWeek;
  label: string;
  /** e.g. "12 May". */
  dateLabel: string;
  playlistName: string | null;
  /** Human summary of when it plays - "Loops all day", "09:00-17:00". */
  when: string;
}

export interface SignagePlanContentPageProps {
  screens: PlanScreen[];
  selectedScreenId: string | null;
  onSelectScreen: (id: string) => void;
  weekLabel: string;
  weekNumber: number;
  days: PlanDay[];
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onAddPlaylist: (day: DayOfWeek) => void;
  onChangePlaylist: (day: DayOfWeek) => void;
  onClearDay: (day: DayOfWeek) => void;
  /**
   * Opens the SCHEDULE builder - laying existing playlists across a day.
   * Making a playlist is the Playlists tab; these are different actions and
   * naming them both "playlist" sent operators to the wrong one.
   */
  onCreateSchedule: () => void;

  /* -- the Playlists tab ------------------------------------------------- */

  /** Every loop schedulable here: this scope's own plus what it inherits. */
  playlists: SignagePlaylist[];
  /** The library, for thumbnails and clip lengths. */
  assets: AssetSummary[];
  /** Which scope is being authored - decides what is editable vs borrowed. */
  scope: SignageScopeRef;
  onNewPlaylist: () => void;
  onEditPlaylist: (playlist: SignagePlaylist) => void;
  /** How a borrowed loop becomes an editable one. */
  onDuplicatePlaylist: (playlist: SignagePlaylist) => void;
  onDeletePlaylist: (playlist: SignagePlaylist) => void;

  /* -- the Schedules tab ------------------------------------------------- */

  /** Every day plan in scope - what the Scheduling tab assigns to a weekday. */
  dayPlans: SignageDayPlan[];
  onEditSchedule: (plan: SignageDayPlan) => void;
  onDeleteSchedule: (plan: SignageDayPlan) => void;

  /** Surfaced inline: a failed save must not look like a successful one. */
  error?: string | null;
  busy?: boolean;
}

/**
 * The loops in scope, and everything you can do to one.
 *
 * Both a location's own playlists and the ones it borrows are listed, because
 * both can be SCHEDULED - but only the owned ones can be edited in place. A
 * borrowed loop offers a duplicate instead, which is the only honest way to
 * "change" something another location is also playing.
 *
 * ⚠️ EFFECT CHANNELS ARE NOT LISTED HERE. Nenda's venue channels are playlists
 * in the database and are not playlists in the product: they are browsed on
 * the Channels page, scheduled from there, and read-only to a customer
 * (Sigge, 2026-08-31). Every action this panel offers - edit, duplicate,
 * delete - is either meaningless or refused on one, so listing 54 of them
 * buried a location's own handful of loops under rows that do nothing.
 *
 * The SCHEDULE builder still sees them, because scheduling is exactly what
 * they are for. This is about which surface manages what, not about scope.
 */
function PlaylistsPanel({
  playlists,
  assets,
  scope,
  query,
  onNew,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  playlists: SignagePlaylist[];
  assets: AssetSummary[];
  scope: SignageScopeRef;
  query: string;
  onNew: () => void;
  onEdit: (p: SignagePlaylist) => void;
  onDuplicate: (p: SignagePlaylist) => void;
  onDelete: (p: SignagePlaylist) => void;
}) {
  const library = useMemo(() => byUrl(assets), [assets]);
  const own = useMemo(() => playlists.filter((p) => !p.effect), [playlists]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? own.filter((p) => p.name.toLowerCase().includes(q)) : own;
  }, [own, query]);

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
          {own.length} {own.length === 1 ? "playlist" : "playlists"} available
          here
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNew}
          data-testid="new-playlist"
        >
          New playlist
        </Button>
      </Stack>

      {shown.length === 0 ? (
        <Box
          sx={{
            border: `1px dashed ${tokens.divider}`,
            borderRadius: "12px",
            p: 5,
            textAlign: "center",
            bgcolor: tokens.surface,
          }}
        >
          <Typography sx={{ fontWeight: 600, color: tokens.ink }}>
            {query ? `Nothing matches “${query}”.` : "No playlists yet"}
          </Typography>
          {!query && (
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mt: 0.5 }}>
              A playlist is the loop a screen plays. Build one from the media
              you have uploaded, then schedule it on the Scheduling tab.
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          }}
        >
          {shown.map((p) => {
            const owned = isOwnedBy(p, scope);
            const total = loopSeconds(p.items, p.defaultItemSeconds, library);
            const cover = p.items
              .map((it) => thumbnailFor(library.get(it.assetUrl)))
              .find(Boolean);
            return (
              <Box
                key={p.id}
                sx={{
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: "12px",
                  bgcolor: tokens.surface,
                  p: 2,
                  display: "flex",
                  gap: 1.5,
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 54,
                    flexShrink: 0,
                    borderRadius: "8px",
                    overflow: "hidden",
                    bgcolor: tokens.rowAlt,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  {cover ? (
                    <Box
                      component="img"
                      src={cover}
                      alt=""
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <PlaylistPlayIcon sx={{ color: tokens.inkFaint }} />
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <Typography
                      noWrap
                      sx={{ fontWeight: 600, color: tokens.ink }}
                    >
                      {p.name}
                    </Typography>
                    {!owned && (
                      <Chip
                        size="small"
                        label={ownerLabel(p)}
                        sx={{
                          height: 18,
                          fontSize: 11,
                          bgcolor: tokens.rowAlt,
                          color: tokens.inkMuted,
                        }}
                      />
                    )}
                  </Stack>
                  <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                    {p.items.length} {p.items.length === 1 ? "item" : "items"} ·{" "}
                    {durationLabel(total) ?? "00:00"} per loop
                  </Typography>
                </Box>

                {owned ? (
                  <>
                    <Button size="small" onClick={() => onEdit(p)}>
                      Edit
                    </Button>
                    <IconButton
                      size="small"
                      aria-label={`Delete ${p.name}`}
                      onClick={() => onDelete(p)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  // Borrowed. Copying it into this scope is the only way to
                  // change it without changing it for everyone else.
                  <Button size="small" onClick={() => onDuplicate(p)}>
                    Duplicate
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

/**
 * What one block of a day plan plays, in words.
 *
 * A live-channel block (`BE-41`) is labelled from the name stored beside it,
 * because there is no playlist to look the name up in and a catalogue read to
 * draw a chip would be absurd. "Deleted playlist" stays the answer for a
 * playlist block whose loop is gone - it is a real state an operator has to be
 * able to see and fix.
 */
function blockLabel(
  block: SignageDayPlan["blocks"][number],
  nameById: Map<string, string>
): string {
  if (block.contentAssetId) {
    return `${block.channelName?.trim() || "Live channel"} (live)`;
  }
  return (
    (block.playlistId ? nameById.get(block.playlistId) : undefined) ??
    "Deleted playlist"
  );
}

/**
 * The day plans in scope, and what each one actually plays.
 *
 * Without this a schedule was write-only: the builder created one and the only
 * place it ever appeared again was inside the assign-a-day dialog, which is
 * not somewhere you go to check your work.
 *
 * Deleting is safe to offer plainly - the server unassigns a deleted plan from
 * every weekday that used it, so nothing is left pointing at a plan nobody can
 * open. Those days simply become unscheduled, which the confirm says.
 */
function SchedulesPanel({
  dayPlans,
  playlists,
  scope,
  query,
  onNew,
  onEdit,
  onDelete,
}: {
  dayPlans: SignageDayPlan[];
  playlists: SignagePlaylist[];
  scope: SignageScopeRef;
  query: string;
  onNew: () => void;
  onEdit: (p: SignageDayPlan) => void;
  onDelete: (p: SignageDayPlan) => void;
}) {
  const nameById = useMemo(
    () => new Map(playlists.map((p) => [p.id, p.name])),
    [playlists]
  );
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? dayPlans.filter((p) => p.name.toLowerCase().includes(q))
      : dayPlans;
  }, [dayPlans, query]);

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
          {dayPlans.length} {dayPlans.length === 1 ? "schedule" : "schedules"}{" "}
          available here
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNew}
          data-testid="new-schedule"
        >
          Create schedule
        </Button>
      </Stack>

      {shown.length === 0 ? (
        <Box
          sx={{
            border: `1px dashed ${tokens.divider}`,
            borderRadius: "12px",
            p: 5,
            textAlign: "center",
            bgcolor: tokens.surface,
          }}
        >
          <Typography sx={{ fontWeight: 600, color: tokens.ink }}>
            {query ? `Nothing matches “${query}”.` : "No schedules yet"}
          </Typography>
          {!query && (
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mt: 0.5 }}>
              A schedule spreads playlists across the hours of a day. You only
              need one if a screen should show different things at different
              times - otherwise assign a playlist straight to the day.
            </Typography>
          )}
        </Box>
      ) : (
        <Stack gap={1.5}>
          {shown.map((p) => {
            const owned = isOwnedBy(p, scope);
            return (
              <Box
                key={p.id}
                sx={{
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: "12px",
                  bgcolor: tokens.surface,
                  p: 2,
                }}
              >
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: "8px",
                      bgcolor: tokens.brandTintSoft,
                      display: "grid",
                      placeItems: "center",
                      color: tokens.brand,
                    }}
                  >
                    <ScheduleIcon />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                      <Typography
                        noWrap
                        sx={{ fontWeight: 600, color: tokens.ink }}
                      >
                        {p.name}
                      </Typography>
                      {!owned && (
                        <Chip
                          size="small"
                          label={ownerLabel(p)}
                          sx={{
                            height: 18,
                            fontSize: 11,
                            bgcolor: tokens.rowAlt,
                            color: tokens.inkMuted,
                          }}
                        />
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                      {describePlan(p)}
                    </Typography>
                  </Box>
                  {owned ? (
                    <>
                      <Button size="small" onClick={() => onEdit(p)}>
                        Edit
                      </Button>
                      <IconButton
                        size="small"
                        aria-label={`Delete ${p.name}`}
                        onClick={() => onDelete(p)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Chip
                      size="small"
                      label="Read only"
                      sx={{
                        height: 20,
                        fontSize: 11,
                        bgcolor: tokens.rowAlt,
                        color: tokens.inkMuted,
                      }}
                    />
                  )}
                </Stack>

                {/* What it actually plays, hour by hour. The point of opening
                    this tab is checking your work, so the blocks are visible
                    without a second click. */}
                {p.blocks.length > 0 && (
                  <Stack
                    direction="row"
                    gap={0.75}
                    flexWrap="wrap"
                    sx={{ mt: 1.5, pl: 7 }}
                  >
                    {[...p.blocks]
                      .sort((a, b) => a.start.localeCompare(b.start))
                      .map((b, i) => (
                        <Chip
                          key={`${b.playlistId ?? b.contentAssetId}-${i}`}
                          size="small"
                          icon={<LoopIcon sx={{ fontSize: 13 }} />}
                          label={`${b.start}–${b.end} · ${blockLabel(
                            b,
                            nameById
                          )}`}
                          sx={{
                            height: 22,
                            fontSize: 11,
                            bgcolor: tokens.rowAlt,
                            color: tokens.ink,
                            "& .MuiChip-icon": { color: tokens.inkMuted },
                          }}
                        />
                      ))}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

const DAY_LABELS: { day: DayOfWeek; label: string }[] = [
  { day: "mon", label: "Monday" },
  { day: "tue", label: "Tuesday" },
  { day: "wed", label: "Wednesday" },
  { day: "thu", label: "Thursday" },
  { day: "fri", label: "Friday" },
  { day: "sat", label: "Saturday" },
  { day: "sun", label: "Sunday" },
];

/* -------------------------------------------------------------------------- */

/**
 * How a screen is hung, said plainly.
 *
 * Content built for one orientation looks wrong on the other - a 16:9 clip on
 * a portrait totem is a stripe between two black bands - so this has to be
 * visible while choosing what to schedule, not buried in the device page. The
 * only signal is the resolution the panel reports, so a screen that has never
 * checked in says nothing rather than guessing landscape.
 */
function ScreenOrientation({
  resolution,
  size = "chip",
}: {
  resolution?: string;
  size?: "chip" | "text";
}) {
  const orientation = orientationOfResolution(resolution);
  if (!orientation) return null;

  const label = ORIENTATION_LABELS[orientation];
  const Icon =
    orientation === "portrait" ? CropPortraitIcon : CropLandscapeIcon;

  if (size === "text") {
    return (
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Icon sx={{ fontSize: 16, color: tokens.brand }} />
        <Typography sx={{ fontSize: 12, color: tokens.brand, fontWeight: 600 }}>
          {label}
        </Typography>
      </Stack>
    );
  }

  return (
    <Chip
      icon={<Icon sx={{ fontSize: 13 }} />}
      label={orientation === "custom" ? label : label.split(" ")[0]}
      size="small"
      title={resolution}
      sx={{
        mt: 0.25,
        height: 20,
        fontSize: 11,
        bgcolor: tokens.brandTintSoft,
        color: tokens.brand,
        "& .MuiChip-icon": { color: tokens.brand, ml: 0.5 },
      }}
    />
  );
}

function ScreenListItem({
  screen,
  selected,
  onClick,
}: {
  screen: PlanScreen;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      aria-pressed={selected}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        cursor: "pointer",
        borderRadius: "8px",
        border: `1px solid ${selected ? tokens.brand : "transparent"}`,
        bgcolor: selected ? tokens.brandTintSoft : "transparent",
        borderBottom: selected
          ? `1px solid ${tokens.brand}`
          : `1px solid ${tokens.divider}`,
        "&:hover": { bgcolor: tokens.brandTintSoft },
      }}
    >
      <TvOutlinedIcon sx={{ color: tokens.inkMuted }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          noWrap
          sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink }}
        >
          {screen.name}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
          {screen.tag && (
            <Chip
              label={screen.tag}
              size="small"
              sx={{
                mt: 0.25,
                height: 20,
                fontSize: 11,
                bgcolor: tokens.rowAlt,
                color: tokens.inkMuted,
              }}
            />
          )}
          <ScreenOrientation resolution={screen.resolution} />
        </Stack>
      </Box>
      {selected && <ChevronRightIcon sx={{ color: tokens.brand }} />}
    </Box>
  );
}

function DayRow({
  entry,
  onAdd,
  onChange,
  onClear,
}: {
  entry: PlanDay;
  onAdd: () => void;
  onChange: () => void;
  onClear: () => void;
}) {
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const empty = entry.playlistName === null;

  return (
    <Stack direction="row" alignItems="stretch" gap={2}>
      <Box sx={{ width: 96, flexShrink: 0, pt: 1.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink }}>
          {entry.label}
        </Typography>
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          {entry.dateLabel}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1.5,
          borderRadius: "8px",
          // An unscheduled day is drawn as an invitation, not as an error: a
          // dashed outline reads "you can put something here", where a solid
          // card would read "something is here".
          border: empty
            ? `1px dashed ${tokens.divider}`
            : `1px solid ${tokens.divider}`,
          bgcolor: empty ? "transparent" : tokens.surface,
        }}
      >
        {empty ? (
          <Stack gap={0.5}>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <EventBusyOutlinedIcon
                sx={{ fontSize: 16, color: tokens.inkFaint }}
              />
              <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
                No playlist
              </Typography>
            </Stack>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={onAdd}
              sx={{ alignSelf: "flex-start", ml: -0.5 }}
            >
              Add playlist
            </Button>
          </Stack>
        ) : (
          <Stack gap={0.5}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: tokens.brand,
                }}
              />
              <Typography
                sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink }}
              >
                {entry.playlistName}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <LoopIcon sx={{ fontSize: 15, color: tokens.inkFaint }} />
              <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                {entry.when}
              </Typography>
            </Stack>
          </Stack>
        )}

        <IconButton
          size="small"
          onClick={(e) => setMenuEl(e.currentTarget)}
          aria-label={`Actions for ${entry.label}`}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuEl}
          open={Boolean(menuEl)}
          onClose={() => setMenuEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              setMenuEl(null);
              onChange();
            }}
          >
            <SwapHorizIcon fontSize="small" style={{ marginRight: 8 }} />
            Change playlist
          </MenuItem>
          <MenuItem
            disabled={empty}
            onClick={() => {
              setMenuEl(null);
              onClear();
            }}
            sx={{ color: tokens.error }}
          >
            <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
            Remove scheduling
          </MenuItem>
        </Menu>
      </Box>
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */

export function SignagePlanContentPage(props: SignagePlanContentPageProps) {
  const {
    screens,
    selectedScreenId,
    onSelectScreen,
    weekLabel,
    weekNumber,
    days,
    onPrevWeek,
    onNextWeek,
    onAddPlaylist,
    onChangePlaylist,
    onClearDay,
    onCreateSchedule,
    playlists,
    assets,
    scope,
    onNewPlaylist,
    onEditPlaylist,
    onDuplicatePlaylist,
    onDeletePlaylist,
    dayPlans,
    onEditSchedule,
    onDeleteSchedule,
    error,
    busy,
  } = props;

  const [tab, setTab] = useState<"scheduling" | "playlists" | "schedules">(
    "scheduling"
  );
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return screens;
    return screens.filter((s) => s.name.toLowerCase().includes(q));
  }, [screens, query]);

  const selected = screens.find((s) => s.id === selectedScreenId) ?? null;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Signage"
        title="Plan content"
        subtitle="Schedule playlists across the week - for one screen, or for a whole group of them. A slide is scheduled by adding it to a playlist first (Playlists → Add slide)."
      />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, borderBottom: `1px solid ${tokens.divider}` }}
      >
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="scheduling" label="Scheduling" />
          <Tab value="playlists" label="Playlists" />
          <Tab value="schedules" label="Schedules" />
        </Tabs>
        <TextField
          size="small"
          placeholder={
            tab === "playlists"
              ? "Search playlists…"
              : tab === "schedules"
                ? "Search schedules…"
                : "Search screens…"
          }
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: 260, mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {tab === "schedules" ? (
        <SchedulesPanel
          dayPlans={dayPlans}
          playlists={playlists}
          scope={scope}
          query={query}
          onNew={onCreateSchedule}
          onEdit={onEditSchedule}
          onDelete={onDeleteSchedule}
        />
      ) : tab === "playlists" ? (
        <PlaylistsPanel
          playlists={playlists}
          assets={assets}
          scope={scope}
          query={query}
          onNew={onNewPlaylist}
          onEdit={onEditPlaylist}
          onDuplicate={onDuplicatePlaylist}
          onDelete={onDeletePlaylist}
        />
      ) : (
        <Stack direction={{ xs: "column", md: "row" }} gap={3}>
          {/* Screen picker. */}
          <Box
            sx={{
              width: { xs: "100%", md: 320 },
              flexShrink: 0,
              border: `1px solid ${tokens.divider}`,
              borderRadius: "12px",
              bgcolor: tokens.surface,
              p: 2,
              alignSelf: "flex-start",
            }}
          >
            <Typography
              sx={{ fontSize: 16, fontWeight: 700, color: tokens.ink }}
            >
              Schedule for
            </Typography>
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mb: 1.5 }}>
              {screens.filter((s) => s.kind !== "group").length} screens
              {screens.some((s) => s.kind === "group")
                ? ` · ${screens.filter((s) => s.kind === "group").length} groups`
                : ""}
            </Typography>

            <Stack>
              {/*
                Groups get their own heading rather than sitting unlabelled
                among the screens. A week written on a group reaches every
                screen in it, and a row that looks exactly like a screen while
                doing something fifty times larger is the one mistake this
                panel can make.
              */}
              {filtered.some((s) => s.kind === "group") && (
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    color: tokens.inkMuted,
                    mt: 0.5,
                    mb: 0.5,
                  }}
                >
                  Groups
                </Typography>
              )}
              {filtered
                .filter((s) => s.kind === "group")
                .map((s) => (
                  <ScreenListItem
                    key={s.id}
                    screen={s}
                    selected={s.id === selectedScreenId}
                    onClick={() => onSelectScreen(s.id)}
                  />
                ))}
              {filtered.some((s) => s.kind === "group") &&
                filtered.some((s) => s.kind !== "group") && (
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      color: tokens.inkMuted,
                      mt: 1.5,
                      mb: 0.5,
                    }}
                  >
                    Screens
                  </Typography>
                )}
              {filtered
                .filter((s) => s.kind !== "group")
                .map((s) => (
                  <ScreenListItem
                    key={s.id}
                    screen={s}
                    selected={s.id === selectedScreenId}
                    onClick={() => onSelectScreen(s.id)}
                  />
                ))}
              {filtered.length === 0 && (
                <Typography
                  sx={{ fontSize: 13, color: tokens.inkMuted, py: 2 }}
                >
                  {query ? (
                    <>No screens match “{query}”.</>
                  ) : (
                    // The likeliest reason this page is empty is that no screen
                    // has been made a signage screen yet - so say that, rather
                    // than leaving a blank panel that reads as broken.
                    <>
                      No signage screens here yet. Open a screen under Places
                      &amp; Screens and set its role to Signage.
                    </>
                  )}
                </Typography>
              )}
            </Stack>

            <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mt: 2 }}>
              Showing {filtered.length} of {screens.length}
            </Typography>
          </Box>

          {/* Week grid. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {!selected ? (
              <Box
                sx={{
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: "12px",
                  p: 5,
                  textAlign: "center",
                  bgcolor: tokens.surface,
                }}
              >
                <Typography sx={{ fontWeight: 600, color: tokens.ink }}>
                  Pick a screen to plan
                </Typography>
              </Box>
            ) : (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={{ mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: "8px",
                      bgcolor: tokens.brandTintSoft,
                      display: "grid",
                      placeItems: "center",
                      color: tokens.brand,
                    }}
                  >
                    <TvOutlinedIcon />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: 20, fontWeight: 700, color: tokens.ink }}
                    >
                      {selected.name}
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={1}>
                      {selected.tag && (
                        <Chip
                          label={selected.tag}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            bgcolor: tokens.rowAlt,
                            color: tokens.inkMuted,
                          }}
                        />
                      )}
                      <ScreenOrientation
                        resolution={selected.resolution}
                        size="text"
                      />
                      {selected.resolution && (
                        <Typography
                          sx={{ fontSize: 12, color: tokens.inkMuted }}
                        >
                          {selected.resolution}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  {/* What is this wall doing right now? Asked here, where the
                      week is on screen, and answered by the same resolver the
                      panel is answered with - including "nothing", which is
                      what a screen with a playlist but no schedule shows. */}
                  <Button
                    variant="outlined"
                    endIcon={<OpenInNewIcon />}
                    onClick={() =>
                      openSignagePreview(selected.premiseId, selected.id)
                    }
                    data-testid="signage-preview-screen"
                  >
                    Preview
                  </Button>
                  {/* A schedule is built FROM playlists, so offering it with
                      none in scope is offering a dead end - the builder opens
                      onto an empty list with nothing to place. Until there is
                      something to schedule, the primary action is making it. */}
                  {playlists.length === 0 ? (
                    <Button
                      variant="contained"
                      endIcon={<AddIcon />}
                      onClick={onNewPlaylist}
                    >
                      New playlist
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      endIcon={<AddIcon />}
                      onClick={onCreateSchedule}
                    >
                      Create schedule
                    </Button>
                  )}
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Typography
                    sx={{ fontSize: 18, fontWeight: 700, color: tokens.ink }}
                  >
                    Schedule for week {weekNumber}
                  </Typography>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <IconButton
                      size="small"
                      onClick={onPrevWeek}
                      aria-label="Previous week"
                      sx={{ border: `1px solid ${tokens.divider}` }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Box
                      sx={{
                        px: 2,
                        py: 0.75,
                        border: `1px solid ${tokens.divider}`,
                        borderRadius: "8px",
                        fontSize: 14,
                        color: tokens.ink,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {weekLabel}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={onNextWeek}
                      aria-label="Next week"
                      sx={{ border: `1px solid ${tokens.divider}` }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                {error && (
                  <Typography
                    sx={{ fontSize: 13, color: tokens.error, mb: 1.5 }}
                  >
                    {error}
                  </Typography>
                )}

                <Stack gap={1.5} sx={{ opacity: busy ? 0.6 : 1 }}>
                  {days.map((d) => (
                    <DayRow
                      key={d.day}
                      entry={d}
                      onAdd={() => onAddPlaylist(d.day)}
                      onChange={() => onChangePlaylist(d.day)}
                      onClear={() => onClearDay(d.day)}
                    />
                  ))}
                </Stack>
              </>
            )}
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
}

export { DAY_LABELS };

/* -------------------------------------------------------------------------- */
/* Container                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Wires the week grid to real assignments.
 *
 * The screen list spans one location or the whole customer; the week shown is
 * the SELECTED screen's own `weekly` map, resolved against the day plans and
 * playlists visible in scope.
 *
 * Assigning a day writes `SignageAssignment.weekly` for that screen. Choosing a
 * single playlist rather than a schedule creates - or reuses - a day plan
 * holding one 00:00-24:00 block, so "loop this all day" never forces an
 * operator through the builder.
 */
export default function SignagePlanContentContainer({
  premiseId,
  companyId,
}: {
  /** Exactly one is set: a location, or the whole customer. */
  premiseId?: string;
  companyId?: string;
}) {
  const [screens, setScreens] = useState<PlanScreen[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [builderOpen, setBuilderOpen] = useState(false);
  /** The schedule being edited, or null while building a new one. */
  const [editingPlan, setEditingPlan] = useState<SignageDayPlan | null>(null);
  const [dayPlans, setDayPlans] = useState<SignageDayPlan[]>([]);
  const [playlists, setPlaylists] = useState<SignagePlaylist[]>([]);
  const [weekly, setWeekly] = useState<Partial<Record<DayOfWeek, string>>>({});
  const [pickerDay, setPickerDay] = useState<DayOfWeek | null>(null);
  const [assets, setAssets] = useState<AssetSummary[]>([]);
  const [playlistOpen, setPlaylistOpen] = useState(false);
  /** The playlist being edited, or null while creating one. */
  const [editingPlaylist, setEditingPlaylist] =
    useState<SignagePlaylist | null>(null);
  const [savingPlaylist, setSavingPlaylist] = useState(false);
  // The thrown error itself, not a sentence: the dialog names a refused title
  // by the name the operator knows it by. See `SignagePlaylistDialog.error`.
  const [playlistError, setPlaylistError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped after any write, to re-pull the lists a save may have changed.
  const [tick, setTick] = useState(0);

  const scope: SignageScopeRef = premiseId
    ? { kind: "premise", premiseId }
    : { kind: "company", companyId: companyId ?? "" };
  const scopeKey = premiseId ?? companyId ?? "";

  const selected = screens.find((s) => s.id === selectedId) ?? null;

  /* -- screens ---------------------------------------------------------- */

  useEffect(() => {
    let alive = true;

    // Shared with the campaign dialog, which books the same screens - one
    // definition of "the signage screens in this scope", not two that drift.
    const load = async (): Promise<PlanScreen[]> => {
      const [screenRows, groupRows] = await Promise.all([
        listSignageScreensIn(scope),
        // A group is a scheduling target too: one week, inherited by every
        // member, instead of the same week typed out fifty times.
        foyerApi.listSignageGroupsIn(scope).catch(() => []),
      ]);
      return [
        // Groups FIRST. They are the broad stroke - you decide what the
        // treadmills play, then go and make exceptions - and a list that opens
        // on fifty screens buries the five groups underneath them.
        ...groupRows.map(
          (g): PlanScreen => ({
            id: g.id,
            kind: "group",
            // A group can span locations, so it has no premise to be keyed by.
            premiseId: "",
            name: g.name,
            tag: `${g.screenIds.length} ${
              g.screenIds.length === 1 ? "screen" : "screens"
            }`,
          })
        ),
        ...screenRows.map(
          (s): PlanScreen => ({
            id: s.id,
            kind: "screen",
            premiseId: s.premiseId,
            name: s.name,
            tag: s.premiseName || "Signage",
            resolution: s.resolution,
          })
        ),
      ];
    };

    load()
      .then((mapped) => {
        if (!alive) return;
        setScreens(mapped);
        setSelectedId((cur) => cur ?? mapped[0]?.id ?? null);
      })
      .catch(() => alive && setScreens([]));

    return () => {
      alive = false;
    };
    // `scope` is rebuilt each render from exactly these two.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiseId, companyId]);

  /* -- schedules + playlists in scope ------------------------------------ */

  useEffect(() => {
    let alive = true;
    Promise.all([
      foyerApi.listSignageDayPlansIn(scope).catch(() => []),
      foyerApi.listSignagePlaylistsIn(scope).catch(() => []),
    ]).then(([plans, lists]) => {
      if (!alive) return;
      setDayPlans(plans);
      setPlaylists(lists);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey, tick]);

  /* -- the media library, for thumbnails and clip lengths ---------------- */

  useEffect(() => {
    let alive = true;
    const layer: MediaLayer = premiseId
      ? { kind: "premise", premiseId }
      : { kind: "company", companyId: companyId ?? "" };
    // Global too: a playlist may hold a curated file, and a row with no
    // thumbnail looks like a broken item rather than a borrowed one.
    Promise.all([
      foyerApi.listAssets(layer).catch(() => []),
      foyerApi.listAssets({ kind: "global" }).catch(() => []),
    ]).then(([own, global]) => {
      if (alive) setAssets([...own, ...global]);
    });
    return () => {
      alive = false;
    };
  }, [premiseId, companyId, tick]);

  /* -- playlist authoring ------------------------------------------------ */

  const savePlaylist = async (patch: Partial<SignagePlaylist>) => {
    setSavingPlaylist(true);
    setPlaylistError(null);
    try {
      if (editingPlaylist) {
        await foyerApi.updateSignagePlaylistIn(
          scope,
          editingPlaylist.id,
          patch
        );
      } else {
        await foyerApi.createSignagePlaylistIn(scope, patch);
      }
      setPlaylistOpen(false);
      setTick((t) => t + 1);
    } catch (e) {
      setPlaylistError(e);
    } finally {
      setSavingPlaylist(false);
    }
  };

  const duplicatePlaylist = async (p: SignagePlaylist) => {
    try {
      // Copies the CONTENT into this scope, never the identity - the copy is a
      // new record here, so editing it cannot reach back to the original.
      await foyerApi.createSignagePlaylistIn(scope, {
        name: `${p.name} (copy)`,
        items: p.items,
        defaultItemSeconds: p.defaultItemSeconds,
        soundEnabled: p.soundEnabled,
      });
      setTick((t) => t + 1);
    } catch {
      setError("Could not duplicate that playlist.");
    }
  };

  const removeSchedule = async (p: SignageDayPlan) => {
    // The server unassigns it from every weekday that used it, so the only
    // consequence to warn about is those days going quiet.
    const ok = window.confirm(
      `Delete “${p.name}”? Any day using it becomes unscheduled.`
    );
    if (!ok) return;
    try {
      await foyerApi.deleteSignageDayPlanIn(scope, p.id);
      setTick((t) => t + 1);
    } catch {
      setError("Could not delete that schedule.");
    }
  };

  const removePlaylist = async (p: SignagePlaylist) => {
    try {
      await foyerApi.deleteSignagePlaylistIn(scope, p.id);
      setTick((t) => t + 1);
    } catch {
      setError("Could not delete that playlist.");
    }
  };

  /* -- the selected screen's week ---------------------------------------- */

  useEffect(() => {
    let alive = true;
    if (!selected) {
      setWeekly({});
      return;
    }
    const load =
      selected.kind === "group"
        ? foyerApi.getSignageGroupAssignmentIn(scope, selected.id)
        : foyerApi.getSignageAssignment(selected.premiseId, selected.id);
    load
      .then((a) => alive && setWeekly(a?.weekly ?? {}))
      .catch(() => alive && setWeekly({}));
    return () => {
      alive = false;
    };
    // Keyed on the screen's identity, not the object: `selected` is recomputed
    // on every render, so depending on it would refetch the week continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, selected?.premiseId, selected?.kind, tick]);

  /* -- week window ------------------------------------------------------- */

  const { weekLabel, weekNumber, dayDates } = useMemo(() => {
    const now = new Date();
    const monday = new Date(now);
    const dow = (monday.getDay() + 6) % 7; // Monday = 0
    monday.setDate(monday.getDate() - dow + weekOffset * 7);
    monday.setHours(0, 0, 0, 0);

    const dates = DAY_LABELS.map((_, i) => {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      return d;
    });
    const fmt = (d: Date) =>
      d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });

    // ISO week number.
    const t = new Date(monday);
    t.setDate(t.getDate() + 3);
    const firstThursday = new Date(t.getFullYear(), 0, 4);
    const week =
      1 +
      Math.round(
        (t.getTime() - firstThursday.getTime()) / 86_400_000 / 7 -
          ((firstThursday.getDay() + 6) % 7) / 7
      );

    return {
      weekLabel: `${fmt(dates[0]!)} – ${fmt(dates[6]!)} ${dates[6]!.getFullYear()}`,
      weekNumber: week,
      dayDates: dates,
    };
  }, [weekOffset]);

  const planById = useMemo(
    () => new Map(dayPlans.map((p) => [p.id, p])),
    [dayPlans]
  );

  const days: PlanDay[] = DAY_LABELS.map((d, i) => {
    const plan = weekly[d.day] ? planById.get(weekly[d.day]!) : undefined;
    return {
      day: d.day,
      label: d.label,
      dateLabel: dayDates[i]!.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      }),
      // A weekday pointing at a plan that no longer exists is shown as empty
      // rather than as a broken name - the assignment is stale either way, and
      // deleting a plan already unassigns it.
      playlistName: plan?.name ?? null,
      when: plan ? describePlan(plan) : "",
    };
  });

  /* -- writes ------------------------------------------------------------ */

  const saveWeekly = async (next: Partial<Record<DayOfWeek, string>>) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    const previous = weekly;
    setWeekly(next); // optimistic - the grid should not lag a click
    try {
      if (selected.kind === "group") {
        await foyerApi.putSignageGroupAssignmentIn(scope, selected.id, {
          weekly: next,
        });
      } else {
        await foyerApi.putSignageAssignment(selected.premiseId, selected.id, {
          weekly: next,
        });
      }
      setTick((t) => t + 1);
    } catch (e) {
      setWeekly(previous);
      setError((e as Error).message || "Could not save the schedule.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Reuse an existing all-day plan for this playlist, or make one.
   *
   * Without the reuse, scheduling the same loop on seven days would litter the
   * account with seven identical single-block plans, all of them showing up in
   * the picker.
   */
  const allDayPlanFor = async (p: SignagePlaylist): Promise<string> => {
    const existing = dayPlans.find(
      (dp) =>
        dp.blocks.length === 1 &&
        dp.blocks[0]!.playlistId === p.id &&
        dp.blocks[0]!.start === "00:00" &&
        dp.blocks[0]!.end === "24:00"
    );
    if (existing) return existing.id;
    const created = await foyerApi.createSignageDayPlanIn(scope, {
      name: p.name,
      blocks: [{ start: "00:00", end: "24:00", playlistId: p.id }],
    });
    setDayPlans((prev) => [created, ...prev]);
    return created.id;
  };

  return (
    <>
      <SignagePlanContentPage
        screens={screens}
        selectedScreenId={selectedId}
        onSelectScreen={setSelectedId}
        weekLabel={weekLabel}
        weekNumber={weekNumber}
        days={days}
        error={error}
        busy={busy}
        onPrevWeek={() => setWeekOffset((w) => w - 1)}
        onNextWeek={() => setWeekOffset((w) => w + 1)}
        onAddPlaylist={setPickerDay}
        onChangePlaylist={setPickerDay}
        onClearDay={(day) => {
          const next = { ...weekly };
          delete next[day];
          void saveWeekly(next);
        }}
        onCreateSchedule={() => {
          setEditingPlan(null);
          setBuilderOpen(true);
        }}
        dayPlans={dayPlans}
        onEditSchedule={(p) => {
          setEditingPlan(p);
          setBuilderOpen(true);
        }}
        onDeleteSchedule={(p) => void removeSchedule(p)}
        playlists={playlists}
        assets={assets}
        scope={scope}
        onNewPlaylist={() => {
          setEditingPlaylist(null);
          setPlaylistError(null);
          setPlaylistOpen(true);
        }}
        onEditPlaylist={(p) => {
          setEditingPlaylist(p);
          setPlaylistError(null);
          setPlaylistOpen(true);
        }}
        onDuplicatePlaylist={(p) => void duplicatePlaylist(p)}
        onDeletePlaylist={(p) => void removePlaylist(p)}
      />

      <SignagePlaylistDialog
        open={playlistOpen}
        onClose={() => setPlaylistOpen(false)}
        playlist={editingPlaylist}
        assets={assets}
        onSave={savePlaylist}
        saving={savingPlaylist}
        error={playlistError}
        // Lets the editor offer live TV where it was sold. At premise scope
        // this answers with that location's own line-up.
        scope={scope}
      />

      <SignageScheduleBuilder
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        scope={scope}
        plan={editingPlan}
        onSaved={() => setTick((t) => t + 1)}
      />

      <SignageDayPlanPicker
        open={pickerDay !== null}
        onClose={() => setPickerDay(null)}
        dayLabel={
          DAY_LABELS.find((d) => d.day === pickerDay)?.label ?? "this day"
        }
        dayPlans={dayPlans}
        playlists={playlists}
        busy={busy}
        onPickPlan={(planId) => {
          if (!pickerDay) return;
          void saveWeekly({ ...weekly, [pickerDay]: planId });
          setPickerDay(null);
        }}
        onPickAllDay={async (p) => {
          if (!pickerDay) return;
          const day = pickerDay;
          setPickerDay(null);
          setBusy(true);
          try {
            const planId = await allDayPlanFor(p);
            await saveWeekly({ ...weekly, [day]: planId });
          } catch (e) {
            setError((e as Error).message || "Could not save the schedule.");
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}
